<ng-container *vsLet="loading$ | async as loading">
  <ng-container *vsLet="isMobile$ | async as isMobile">
    <ng-container *ngIf="plansAndAddons$ | async as plansAndAddons">
      <ng-container *ngIf="loading; else loadedPlansState">
        <div class="flex flex-col gap-6 mt-6 pb-6">
          <div class="h-10 w-72 mx-auto rounded-lg bg-gray-100 animate-pulse"></div>

          <div class="flex gap-4 mx-auto max-w-[90vw] w-full" [class.justify-center]="!isMobile">
            <div
              class="flex flex-col gap-4 px-4 pt-4 pb-6 bg-base-white rounded-xl shadow-sm h-fit"
              [class.w-full]="isMobile"
              [class.w-[429px]]="!isMobile"
            >
              <div class="h-6 w-40 mx-auto rounded bg-gray-100 animate-pulse"></div>
              <div class="h-12 w-52 mx-auto rounded bg-gray-100 animate-pulse"></div>
              <div class="h-4 w-full rounded bg-gray-100 animate-pulse"></div>
              <div class="h-4 w-5/6 rounded bg-gray-100 animate-pulse"></div>
              <div class="h-10 w-56 mx-auto rounded-lg bg-gray-100 animate-pulse"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #loadedPlansState>
        <ng-container *vsLet="selectedPlanId$ | async as selectedPlanId">
          <!--
            VS-896: PLAN -> TERM mode. One selector (plan chip strip) followed by one price card
            per term the selected plan offers. The term picker is gone — each card IS its own
            term selector. Compare with TERM_FIRST below: same 1-selector / N-cards shape, just
            swapped axes.
          -->
          <ng-container *ngIf="planFirst; else termFirst">
            <!--
              Strip is driven by `planFirstPlans$` (a stable, term-independent list of every
              sellable plan in the policy). Cards below come from `planFirstTermCards$` which
              filters to the selected plan and resolves each term's data for side-by-side display.
            -->
            <ng-container *vsLet="planFirstPlans$ | async as planFirstPlans">
              <ng-container *ngIf="planFirstPlans?.length; else noPlans">
                <!-- Plan chip strip -->
                <div class="flex justify-center mt-6">
                  <div
                    class="flex flex-wrap justify-center gap-1 p-1 rounded-lg border border-gray-200 bg-base-white"
                    role="group"
                    aria-label="Choose a plan"
                  >
                    <button
                      *ngFor="let plan of planFirstPlans"
                      vs-button
                      type="button"
                      size="md"
                      [appearance]="selectedPlanId === plan.id ? 'default' : 'clear'"
                      [theme]="selectedPlanId === plan.id ? 'primary' : 'default'"
                      (click)="selectPlanFromStrip(plan.id)"
                    >
                      {{ plan.title }}
                    </button>
                  </div>
                </div>

                <!-- One card per term the selected plan supports -->
                <div class="max-w-min mx-auto overflow-x-auto scrollbar-thin mt-6 pb-6">
                  <div *ngIf="totalPrices$ | async as totalPrices">
                    <!--
                      Switch from `*vsLet` to `*ngIf as` so the template type-checker can narrow
                      `termCards` to a non-null array — required for the mobile-carousel branch
                      which does direct `termCards.length` / `termCards[current]` access.
                    -->
                    <ng-container *ngIf="planFirstTermCards$ | async as termCards; else noPlans">
                      <ng-container *ngIf="termCards.length; else noPlans">
                        <!-- Desktop: row of term cards -->
                        <div *ngIf="!isMobile; else mobilePlanFirst" class="flex gap-4 mx-auto max-w-[90vw]">
                          <ng-container *ngFor="let card of termCards">
                            <ng-container
                              [ngTemplateOutlet]="plan"
                              [ngTemplateOutletContext]="{
                                data: card.data,
                                totalPrices,
                                term: card.term,
                                isTermCard: true,
                              }"
                            ></ng-container>
                          </ng-container>
                        </div>

                        <!--
                          Mobile: carousel pattern reused from TERM_FIRST, one term card at a time.
                          `navigation$` is global and persists across plan-strip clicks — if the
                          user is on card index 2 of a 3-term plan then switches to a 1-term plan,
                          a naive `termCards[current]` would crash. Clamp into range with
                          `Math.min` before dereferencing.
                        -->
                        <ng-template #mobilePlanFirst>
                          <div *vsLet="navigation$ | async as current" class="flex justify-center px-1">
                            <ng-container
                              *ngIf="termCards[clampCarouselIndex(current, termCards.length)] as currentCard"
                            >
                              <ng-container
                                [ngTemplateOutlet]="plan"
                                [ngTemplateOutletContext]="{
                                  data: currentCard.data,
                                  totalPrices,
                                  term: currentCard.term,
                                  isTermCard: true,
                                  current: clampCarouselIndex(current, termCards.length),
                                  last: termCards.length - 1,
                                }"
                              ></ng-container>
                            </ng-container>
                          </div>
                        </ng-template>
                      </ng-container>
                    </ng-container>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </ng-container>

          <!-- TERM -> PLAN mode (default): existing layout, unchanged. -->
          <ng-template #termFirst>
            <!-- Policy Term Picker -->
            <div class="flex justify-center mt-6 pb-6">
              <ws-policy-term-picker
                [policy]="policy"
                [staffView]="staffView"
                [selected]="selectedTerm?.id ?? null"
                [viewablePlansAndTerms]="viewablePlansAndTerms"
                (valueChanges)="selectTerm($event)"
              >
              </ws-policy-term-picker>
            </div>

            <div *ngIf="plansAndAddons.length; else noPlans" class="max-w-min mx-auto overflow-x-auto scrollbar-thin">
              <div *ngIf="totalPrices$ | async as totalPrices">
                <!-- Plan and Addon Picker -->
                <div *ngIf="!isMobile; else mobile" class="flex gap-4 mx-auto max-w-[90vw]">
                  <ng-container *ngFor="let data of plansAndAddons; let planIndex = index">
                    <ng-container
                      [ngTemplateOutlet]="plan"
                      [ngTemplateOutletContext]="{ data, totalPrices, term: selectedTerm, isTermCard: false }"
                    ></ng-container>
                  </ng-container>
                </div>

                <ng-template #mobile>
                  <div *vsLet="navigation$ | async as current" class="flex justify-center px-1">
                    <ng-container *ngIf="plansAndAddons.length >= 1">
                      <ng-container
                        [ngTemplateOutlet]="plan"
                        [ngTemplateOutletContext]="{
                          data: plansAndAddons[current ?? 0],
                          totalPrices,
                          term: selectedTerm,
                          isTermCard: false,
                          current: current,
                          last: plansAndAddons.length - 1,
                        }"
                      ></ng-container>
                    </ng-container>
                  </div>
                </ng-template>
              </div>
            </div>
          </ng-template>
        </ng-container>
      </ng-template>
    </ng-container>

    <!-- No Plans template -->
    <ng-template #noPlans>
      <ws-no-plans-available-card [hideBack]="false" (back)="goBack()"></ws-no-plans-available-card>
    </ng-template>

    <ng-template
      #plan
      let-data="data"
      let-totalPrices="totalPrices"
      let-term="term"
      let-isTermCard="isTermCard"
      let-last="last"
      let-current="current"
    >
      <!-- Plan Card -->
      <div
        *vsLet="totalPrices.totals.get((term?.id || data.termId) + '|' + data.planId) as prices"
        class="flex flex-col gap-4 px-4 pt-4 pb-6 bg-base-white rounded-xl shadow-sm h-fit"
        [class]="isMobile ? 'w-full' : 'w-[429px]'"
        [class.opacity-60]="loading"
        [class.pointer-events-none]="loading"
        [class.border]="!data.eligible"
        [class.border-amber-200]="!data.eligible"
        [class.bg-amber-50\/30]="!data.eligible"
      >
        <!-- Header -->
        <div class="flex flex-col gap-4 items-center">
          <div class="grid grid-cols-[48px_1fr_48px] gap-2 px-4 py-1 border-b w-full items-center">
            <div class="w-12 h-12">
              <button
                *ngIf="isMobile && current !== 0"
                vs-icon-button
                size="lg"
                theme="default"
                appearance="clear"
                (click)="navigation$.next(current - 1)"
              >
                <vs-icon name="chevron-left" size="20"></vs-icon>
              </button>
            </div>

            <!--
              VS-896: in PLAN_FIRST term cards the header is the TERM name (e.g. "1 year") since
              the plan is implied by the chip strip above. In TERM_FIRST we keep the original
              plan-title header (the term is implied by the term-picker above).
            -->
            <div
              class="text-xl flex-grow font-semibold text-gray-900 text-center truncate"
              [vsTooltip]="isTermCard ? term?.title : data.plan.title"
            >
              {{ isTermCard ? term?.title : data.plan.title }}
            </div>

            <div class="w-12 h-12">
              <button
                *ngIf="isMobile && current !== last"
                vs-icon-button
                size="lg"
                theme="default"
                appearance="clear"
                (click)="navigation$.next(current + 1)"
              >
                <vs-icon name="chevron-right" size="20"></vs-icon>
              </button>
            </div>
          </div>

          <div *ngIf="!data.eligible" class="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1">
            <span class="h-1.5 w-1.5 rounded-full bg-amber-600"></span>
            <span class="text-sm font-semibold text-amber-800">{{ config.notAvailable }}</span>
          </div>

          <ng-container [ngTemplateOutlet]="planPricesAndDescription"></ng-container>

          <ng-template #planPricesAndDescription>
            <!--
              `prices` is undefined when the composite key (termId|planId) doesn't resolve — happens
              if the backend hasn't been redeployed with VS-896's termId-stamping. Skip the whole
              price block instead of throwing on `prices.total` access; the card still shows its
              title, description and coverages accordion so the user gets *something* useful.
            -->
            <div
              *ngIf="prices && data.eligible; else noPriceBlock"
              class="flex flex-col gap-4 text-center"
              [class]="data.hasDescription ? 'h-[124px]' : 'h-[80px]'"
            >
              <div class="text-4xl font-semibold h-[80px]">
                <ng-container *ngIf="prices.total && prices.total > 0; else freeTemplate">
                  <!--  Show installment/period price first -->
                  <ng-container *ngIf="prices.installments.length; else totalFirst">
                    <p class="text-center font-normal text-sm text-gray-600">
                      {{ prices.installments.length === 1 ? '' : 'starting at ' }}
                    </p>
                    <span class="text-center">
                      {{
                        installmentTotalWithMarkup(term?.id || data.termId, data.planId, prices.installments[0]) / 100
                          | currency: 'USD' : 'symbol' : '1.2-2'
                      }}
                      <span class="text-lg">
                        /
                        {{
                          prices.installments[0].count
                            | installment: (data.termDuration ?? term?.duration ?? selectedTerm?.duration) || 0
                        }}
                      </span>
                    </span>
                  </ng-container>

                  <!-- fallback to total for one-time only -->
                  <ng-template #totalFirst>
                    <span class="text-center">
                      {{
                        (prices.total + getMarkupCents(term?.id || data.termId, data.planId)) / 100
                          | currency: 'USD' : 'symbol' : '1.2-2'
                      }}
                      <ng-container *ngIf="data.termDuration ?? term?.duration ?? selectedTerm?.duration">
                        <span class="text-lg">
                          / {{ formatTermDuration(data.termDuration ?? term?.duration ?? selectedTerm?.duration) }}
                        </span>
                      </ng-container>
                    </span>
                    <p
                      *ngIf="prices.hasOneTimePayment && !prices.installments.length"
                      class="text-sm text-center font-normal text-gray-600"
                    >
                      one-time payment
                    </p>
                  </ng-template>
                  <br />
                  <!-- Then show final total (smaller and subtle) -->
                  <p
                    *ngIf="prices.installments.length && prices.total && prices.total !== prices.installments[0].total"
                    class="text-2xl text-center text-gray-500 mt-1 inline-block"
                  >
                    {{
                      (prices.total + getMarkupCents(term?.id || data.termId, data.planId)) / 100
                        | currency: 'USD' : 'symbol' : '1.2-2'
                    }}
                    <span class="text-sm">
                      / {{ formatTermDuration(data.termDuration ?? term?.duration ?? selectedTerm?.duration) }}
                    </span>
                  </p>
                </ng-container>

                <ng-template #freeTemplate>
                  <span class="text-center text-black-300">
                    {{
                      (prices.price + getMarkupCents(term?.id || data.termId, data.planId)) / 100
                        | currency: 'USD' : 'symbol' : '1.2-2'
                    }}
                    <ng-container *ngIf="data.termDuration ?? term?.duration ?? selectedTerm?.duration">
                      <span class="text-lg">
                        / {{ formatTermDuration(data.termDuration ?? term?.duration ?? selectedTerm?.duration) }}
                      </span>
                    </ng-container>
                  </span>
                </ng-template>
              </div>

              <div
                class="flex items-center justify-center h-10 max-w-[60ch] gap-1"
                *vsLet="data.plan?.description as description"
              >
                <span
                  class="text-sm text-gray-600 overflow-hidden overflow-ellipsis !whitespace-normal max-h-10 text-center"
                >
                  {{ description?.length > 80 ? description?.substring(0, 80) + '...' : description }}
                </span>

                <vs-icon
                  *ngIf="description?.length > 80"
                  name="info-circle"
                  tooltipStyle="light"
                  tooltipPosition="start"
                  class="text-sm text-gray-600"
                  [vsTooltip]="{ template: descriptionTooltip }"
                ></vs-icon>

                <ng-template #descriptionTooltip>
                  <div class="max-w-[30ch]">
                    {{ description }}
                  </div>
                </ng-template>
              </div>
            </div>

            <div *ngIf="!data.eligible" class="flex flex-col gap-2 text-left w-full max-w-[60ch] px-2 py-1">
              <p class="text-sm font-semibold text-amber-800">This plan isn't available for your asset because:</p>
              <ul class="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li *ngFor="let reason of data.ineligibilityReasons ?? []">
                  {{ formatIneligibilityReason(reason) }}
                  <div *ngIf="formatIneligibilityActualValue(reason) as actualValue" class="text-gray-500 mt-0.5">
                    {{ actualValue }}
                  </div>
                </li>
              </ul>
            </div>

            <!--
              Retail markup quote — admin dashboard only (`staffView`). Lets a seller add their
              markup to this specific card's price before any plan is actually bought. Hiding the
              toggle only hides the input box; the marked-up price above stays visible so the seller
              can still share the screen with the buyer.
            -->
            <div
              *ngIf="data.eligible && staffView && markupAllowed && prices && !hideMarkupEntry"
              class="flex flex-col gap-1 items-center mx-auto w-full max-w-[180px]"
            >
              <label class="text-xs font-medium text-gray-700">Retail Markup</label>
              <div class="flex items-center gap-1 w-full">
                <span class="text-sm text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-center"
                  placeholder="0.00"
                  [value]="getMarkupDollars(term?.id || data.termId, data.planId)"
                  (input)="onMarkupInput(term?.id || data.termId, data.planId, $any($event.target).value)"
                />
              </div>
            </div>

            <!--
              Buy / Confirm CTA is also gated on `prices` being present — same backend-not-redeployed
              edge case as the price display above. `noPriceBlock` is left empty intentionally
              (no fallback skeleton is needed for the CTA — the card just suppresses it).
            -->
            <div *ngIf="prices" class="flex justify-center items-center">
              <!--
                VS-896: PLAN_FIRST term cards must couple "pick this term" and "buy" into one
                action — selectPlanFirstCard handles both atomically so a user can never end up
                in checkout with a stale term selection from a previous card click.
              -->
              <button *ngIf="!data.eligible" vs-button size="lg" class="!w-56" theme="primary" [disabled]="true">
                Not eligible
              </button>

              <button
                *ngIf="prices.total !== null && prices.total !== undefined && (prices.total > 0 || staffView)"
                vs-button
                size="lg"
                class="!w-56"
                theme="primary"
                (click)="isTermCard && term ? selectPlanFirstCard(data.plan, term) : selectPlan(data.plan, true)"
                [disabled]="
                  disabled ||
                  (!data.hasBaseCoverages &&
                    !hasSelectedAddonCoverage(data.planId, term?.id || data.termId, data.addons))
                "
                [class.hidden]="!data.eligible"
              >
                {{ prices.total > 0 ? 'Buy now' : 'Confirm' }}
              </button>

              <a
                *ngIf="
                  data.eligible &&
                  prices.total !== null &&
                  prices.total !== undefined &&
                  prices.total <= 0 &&
                  !staffView
                "
                vs-button
                size="lg"
                class="!w-56"
                theme="primary"
                href="mailto:{{ contactEmail }}"
              >
                Get in touch
              </a>
            </div>

            <p
              *ngIf="
                data.eligible &&
                !data.hasBaseCoverages &&
                !hasSelectedAddonCoverage(data.planId, term?.id || data.termId, data.addons)
              "
              class="text-sm text-gray-600 text-center"
            >
              Select at least one add-on to continue.
            </p>

            <!-- Placeholder when prices unresolved — keeps card height stable across loading states. -->
            <ng-template #noPriceBlock>
              <div *ngIf="data.eligible" class="h-[80px]"></div>
            </ng-template>
          </ng-template>
        </div>

        <div
          *ngIf="
            data.eligible && (data.plan?.planLiabilityLimitPropertyPath || (data.plan?.planLiabilityLimit || 0) > 0)
          "
          class="flex items-center justify-center gap-2 text-sm text-gray-600"
        >
          <span>Plan Liability Limit:</span>
          <span class="font-semibold text-gray-900">
            {{ (data.plan.planLiabilityLimit || 0) / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
          </span>
        </div>

        <vs-accordion *ngIf="data.eligible" #coveragesAccordion [open]="true">
          <ws-coverage-accordion-header
            slot="header"
            header="Coverages"
            [open]="coveragesAccordion.open"
            (toggled)="coveragesAccordion.toggle()"
          >
            <vs-badge slot="post-title" theme="primary">{{ data.baseCoveragesCount }} Included</vs-badge>
          </ws-coverage-accordion-header>

          <div slot="content" class="flex flex-col">
            <p class="px-4 pt-3 pb-1 text-xs text-gray-500">Click a coverage name to view its details.</p>
            <ng-container *ngFor="let group of data.baseGroups; let first = first">
              <div
                [ngClass]="first ? 'py-4' : 'py-2'"
                class="flex justify-between items-center pl-4 border-b border-gray-200 font-normal text-gray-800"
              >
                <p *ngIf="group.coverages.length && first">Coverage</p>
                <p
                  *ngIf="group.coverages.length && first && data.hasLiabilityLimit"
                  vsTooltip="The maximum coverage amount for repairs or replacements that we've got you covered for. Please check your Terms & Conditions after purchasing for the complete details."
                  tooltipMaxWidth="xs"
                  tooltipStyle="light"
                >
                  Limit of Liability
                </p>
              </div>
              <div class="flex justify-between font-bold my-2 pl-4 py-1 items-center">
                <p *ngIf="group.coverages.length">{{ group.group }}</p>
                <ng-container
                  *ngIf="group.liabilityGroups?.length && data.hasLiabilityLimit"
                  [ngTemplateOutlet]="CoverageGroupAggLimit"
                  [ngTemplateOutletContext]="{ groupName: group.group, liabilityGroups: group.liabilityGroups }"
                ></ng-container>
              </div>

              <ng-container *ngFor="let coverage of group.coverages">
                <ng-container *ngIf="coverage.requirement === 'BASE'">
                  <button
                    *vsLet="coverage.liabilityLimit as liabilityLimit"
                    vs-button
                    appearance="clear"
                    theme="default"
                    [rounded]="false"
                    class="!justify-between !w-full !text-start !font-normal"
                    size="md"
                    (click)="CoverageDialog.open()"
                  >
                    <div class="flex items-center gap-2 min-w-0">
                      <span class="truncate overflow-hidden max-w-[220px]">{{ coverage.coverage?.title }}</span>
                      <vs-icon name="info-circle" size="16" class="text-gray-500 flex-shrink-0"></vs-icon>
                    </div>

                    <span *ngIf="liabilityLimit">
                      {{ liabilityLimit / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                    </span>
                    <ng-template #BaseAggLimit>
                      <ng-container
                        [ngTemplateOutlet]="AggLimit"
                        [ngTemplateOutletContext]="{ coverage }"
                      ></ng-container>
                    </ng-template>
                  </button>

                  <ws-claim-coverage-dialog
                    #CoverageDialog
                    [coverage]="coverage.coverage"
                    [deductible]="coverage.deductible"
                    [liabilityLimit]="coverage.liabilityLimit"
                    [notApplicable]="coverage.requirement === 'NOT_APPLICABLE'"
                  ></ws-claim-coverage-dialog>
                </ng-container>
              </ng-container>
            </ng-container>
          </div>
        </vs-accordion>

        <!--
          Addons block leans on `prices.installments` for per-installment math, so gate the whole
          section on `prices` existing. When backend is older and prices unresolved, addons section
          is suppressed entirely — same defensive shape as the price/CTA block above.
        -->
        <ng-container *ngIf="data.eligible && data.addons.length && prices">
          <vs-accordion #addonsAccordion>
            <ws-coverage-accordion-header
              slot="header"
              header="Add-ons"
              [open]="addonsAccordion.open"
              (toggled)="addonsAccordion.toggle()"
            >
              <!-- Total cost of selected Add-ons in badge format -->
              <vs-badge
                slot="post-title"
                theme="success"
                *ngIf="totalPrices.addon_hash[(term?.id || data.termId) + '|' + data.planId]?.total as addonTotal"
              >
                +
                {{ addonTotal / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
              </vs-badge>
            </ws-coverage-accordion-header>

            <div
              slot="content"
              class="flex flex-col gap-2 py-4 border-b"
              *vsLet="value[term?.id || selectedTerm?.id || ''] as value"
            >
              <ng-container *ngFor="let addons of data.addons; let i = index; let last = last">
                <ng-container *ngIf="addons.addonId.startsWith('cov'); else group">
                  <div
                    class="flex flex-col"
                    [class.opacity-50]="
                      !isDependencyMet(data.planId, term?.id || data.termId || '', addons.addonId) &&
                      !(value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId)
                    "
                  >
                    <div class="flex justify-between w-full items-center gap-2 text-sm">
                      <div class="flex items-center gap-1 min-w-0">
                        <input
                          #checkbox
                          vsInput
                          type="checkbox"
                          [checked]="value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId"
                          [disabled]="
                            !isDependencyMet(data.planId, term?.id || data.termId || '', addons.addonId) &&
                            !(value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId)
                          "
                          (change)="
                            checkbox.checked
                              ? isTermCard && term
                                ? selectAddonForTermCard(data.plan, term, addons.addonId)
                                : selectAddon(data.plan, addons.addonId)
                              : isTermCard && term
                                ? deselectAddonForTermCard(term, addons.addon.id)
                                : deselectAddon(addons.addon.id)
                          "
                        />

                        <button (click)="CoverageDialog.open()" class="flex items-center gap-1 min-w-0">
                          <span class="text-start truncate">{{ addons.addon.title }}</span>
                          <vs-icon name="info-circle" size="14" class="text-gray-500 flex-shrink-0"></vs-icon>
                        </button>
                      </div>

                      <span *ngIf="addons.coverages[0].price" class="whitespace-nowrap flex-shrink-0">
                        +
                        <ng-container
                          *ngIf="prices.installments.length === 0 || prices.installments[0]?.count === 1; else monthly"
                        >
                          {{ (addons.coverages[0].price || 0) / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          /one-time payment
                        </ng-container>
                        <ng-template #monthly>
                          {{ (addons.coverages[0].price || 0) / 100 / 12 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          <ng-container *ngIf="prices.installments[0]">
                            /{{
                              prices.installments[0].count
                                | installment: (data.termDuration ?? term?.duration ?? selectedTerm?.duration) || 0
                            }}
                          </ng-container>
                        </ng-template>
                      </span>
                    </div>

                    <!-- Dependency hint -->
                    <div
                      *ngIf="getDependencyHint(data.planId, term?.id || data.termId || '', addons.addonId) as hint"
                      class="ml-6 mt-0.5 flex items-center gap-1"
                    >
                      <vs-icon name="lock-01" size="12" class="text-gray-400 flex-shrink-0"></vs-icon>
                      <span class="text-xs text-gray-400">{{ hint }}</span>
                    </div>
                  </div>

                  <ng-template #AddonAggLimit>
                    <ng-container
                      [ngTemplateOutlet]="AggLimit"
                      [ngTemplateOutletContext]="{ coverage: addons.coverages[0] }"
                    ></ng-container>
                  </ng-template>

                  <ws-preview-group-addons
                    #CoverageDialog
                    [addon]="addons"
                    [checked]="!!value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId"
                    (selected)="
                      $event
                        ? isTermCard && term
                          ? selectAddonForTermCard(data.plan, term, addons.addonId)
                          : selectAddon(data.plan, addons.addonId)
                        : isTermCard && term
                          ? deselectAddonForTermCard(term, addons.addon.id)
                          : deselectAddon(addons.addon.id)
                    "
                  ></ws-preview-group-addons>
                </ng-container>

                <ng-template #group>
                  <div
                    [class.opacity-50]="
                      !isDependencyMet(data.planId, term?.id || data.termId || '', addons.addonId) &&
                      !(value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId)
                    "
                  >
                    <vs-accordion #addonGroupAccordion>
                      <ws-coverage-accordion-header
                        slot="header"
                        [header]="addons.addon.title"
                        [minimize]="true"
                        [checkbox]="true"
                        [select]="!!(value?.addonIds?.includes(addons.addonId) && value?.plan?.id)"
                        [disabled]="
                          !isDependencyMet(data.planId, term?.id || data.termId || '', addons.addonId) &&
                          !(value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId)
                        "
                        [open]="addonGroupAccordion.open"
                        (toggled)="addonGroupAccordion.toggle()"
                        (selected)="
                          $event
                            ? isTermCard && term
                              ? selectAddonForTermCard(data.plan, term, addons.addon.id)
                              : selectAddon(data.plan, addons.addon.id)
                            : isTermCard && term
                              ? deselectAddonForTermCard(term, addons.addon.id)
                              : deselectAddon(addons.addon.id)
                        "
                      >
                        <vs-icon
                          slot="header-end"
                          [vsTooltip]="
                            'To view the limits of liability for ' +
                            addons.addon.title +
                            ', expand this bundle to view the individual coverages'
                          "
                          tooltipMaxWidth="xs"
                          tooltipStyle="light"
                          name="minus"
                        ></vs-icon>
                      </ws-coverage-accordion-header>

                      <div slot="content" class="flex flex-col py-2 pl-5">
                        <ng-container *ngFor="let coverage of addons.coverages; let index = index">
                          <button
                            vs-button
                            size="sm"
                            theme="default"
                            appearance="clear"
                            class="!justify-between !w-full !text-start !font-normal !px-0 !gap-2"
                            [rounded]="false"
                            (click)="CoverageDialog.open()"
                          >
                            <span class="flex items-center gap-1 min-w-0 flex-1">
                              <span class="truncate">{{ coverage.coverage?.title }}</span>
                              <vs-icon
                                name="info-circle"
                                size="14"
                                class="text-gray-500 flex-shrink-0"
                                [vsTooltip]="'View additional addon information'"
                              ></vs-icon>
                            </span>
                            <span *ngIf="coverage.price" class="whitespace-nowrap flex-shrink-0">
                              +
                              {{ coverage.price / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            </span>
                          </button>

                          <ng-template #GroupAddonAggLimit>
                            <ng-container
                              [ngTemplateOutlet]="AggLimit"
                              [ngTemplateOutletContext]="{ coverage }"
                            ></ng-container>
                          </ng-template>

                          <ws-preview-group-addons
                            #CoverageDialog
                            [addon]="addons"
                            [selectedAddon]="index"
                            [checked]="!!value?.addonIds?.includes(addons.addonId) && value?.plan?.id === data.planId"
                            (selected)="
                              $event
                                ? isTermCard && term
                                  ? selectAddonForTermCard(data.plan, term, addons.addonId)
                                  : selectAddon(data.plan, addons.addonId)
                                : isTermCard && term
                                  ? deselectAddonForTermCard(term, addons.addon.id)
                                  : deselectAddon(addons.addon.id)
                            "
                          ></ws-preview-group-addons>
                        </ng-container>
                      </div>
                    </vs-accordion>

                    <!-- Dependency hint for group addons (below the title) -->
                    <div
                      *ngIf="getDependencyHint(data.planId, term?.id || data.termId || '', addons.addonId) as hint"
                      class="mt-1 flex items-center gap-1"
                    >
                      <vs-icon name="lock-01" size="12" class="text-gray-400 flex-shrink-0"></vs-icon>
                      <span class="text-xs text-gray-400">{{ hint }}</span>
                    </div>
                  </div>
                </ng-template>
              </ng-container>
            </div>
          </vs-accordion>

          <div class="flex justify-center items-center">
            <button
              *ngIf="
                data.eligible &&
                addonsAccordion.open &&
                prices.total !== null &&
                prices.total !== undefined &&
                (prices.total > 0 || staffView)
              "
              vs-button
              size="lg"
              class="!w-56"
              theme="primary"
              [disabled]="
                disabled ||
                (!data.hasBaseCoverages && !hasSelectedAddonCoverage(data.planId, term?.id || data.termId, data.addons))
              "
              (click)="isTermCard && term ? selectPlanFirstCard(data.plan, term) : selectPlan(data.plan, true)"
            >
              {{ prices.total > 0 ? 'Buy now' : 'Confirm' }}
            </button>
            <a
              *ngIf="
                data.eligible &&
                addonsAccordion.open &&
                prices.total !== null &&
                prices.total !== undefined &&
                prices.total <= 0 &&
                !staffView
              "
              vs-button
              size="lg"
              class="!w-56"
              theme="primary"
              href="mailto:{{ contactEmail }}"
            >
              Get in touch
            </a>
          </div>
        </ng-container>
      </div>
    </ng-template>
  </ng-container>

  <ng-template #CoverageGroupAggLimit let-groupName="groupName" let-liabilityGroups="liabilityGroups">
    <ng-container *ngFor="let group of liabilityGroups; let index = index">
      <span>&nbsp;Aggregate {{ group.limit / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
    </ng-container>
  </ng-template>

  <ng-template #AggLimit let-coverage="coverage">
    <div class="flex flex-col gap-2 pb-2">
      <div class="text-xs">Aggregate limits for {{ coverage.coverage?.title }}</div>
      <div class="flex flex-col min-w-[200px] outline outline-gray-200 rounded-lg">
        <div
          class="flex justify-between py-3 px-6 text-left text-xs font-medium text-gray-600 bg-base border-b border-gray-200 whitespace-nowrap"
        >
          <div>Title</div>
          <div>Limit</div>
        </div>
        <div class="flex flex-col">
          <ng-container *ngFor="let group of coverage.liabilityGroupObjects; let index = index">
            <div
              class="flex justify-between bg-base py-2 px-6 border-b border-gray-200"
              [class.bg-gray-50]="index % 2 === 1"
            >
              <div>{{ group.title }}</div>
              <div>{{ group.limit / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </ng-template>
</ng-container>
